import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { PropsWithChildren } from 'react';
import { t } from '@/lib/i18n';
import { defaultPrimaryColor } from './lobe-theme';

export type ThemeMode = 'light' | 'dark';
export type LayoutMode = 'side' | 'top' | 'mix';
/** How wide a `top` layout lets content grow; ProLayout ignores it for the other layouts. */
export type ContentWidth = 'Fluid' | 'Fixed';
/** Whether a sider nests its items (`sub`) or groups them under headings (`group`). */
export type SiderMenuType = 'sub' | 'group';

/** The shell's appearance, as the operator set it. */
export type AppSettings = {
  readonly themeMode: ThemeMode;
  readonly primaryColor: string;
  readonly layout: LayoutMode;
  readonly compact: boolean;
  /**
   * The ProLayout knobs that only the development-only appearance drawer exposes.
   *
   * They live in this one object — rather than in the drawer's own state — so that what the
   * drawer shows, what the shell renders and what `恢复默认` resets cannot drift apart. The
   * defaults are the values `app-shell.tsx` used to hard-code.
   */
  readonly contentWidth: ContentWidth;
  readonly fixedHeader: boolean;
  readonly fixSiderbar: boolean;
  readonly splitMenus: boolean;
  readonly siderMenuType: SiderMenuType;
};

export const primaryColorOptions = [
  {
    label: t('品牌蓝'),
    value: defaultPrimaryColor,
    swatchClassName: 'bg-[#2F4CA6]',
  },
  {
    label: t('黑白灰'),
    value: '#222222',
    swatchClassName: 'bg-[#222222]',
  },
  { label: t('拂晓蓝'), value: '#1677ff', swatchClassName: 'bg-[#1677ff]' },
  { label: t('极客蓝'), value: '#2f54eb', swatchClassName: 'bg-[#2f54eb]' },
  { label: t('酱紫'), value: '#722ed1', swatchClassName: 'bg-[#722ed1]' },
  { label: t('薄暮红'), value: '#f5222d', swatchClassName: 'bg-[#f5222d]' },
  { label: t('火山橙'), value: '#fa541c', swatchClassName: 'bg-[#fa541c]' },
  { label: t('明青'), value: '#13c2c2', swatchClassName: 'bg-[#13c2c2]' },
  { label: t('极光绿'), value: '#52c41a', swatchClassName: 'bg-[#52c41a]' },
  { label: t('日暮黄'), value: '#faad14', swatchClassName: 'bg-[#faad14]' },
] as const;

export const layoutOptions = [
  { label: t('侧边菜单'), value: 'side' },
  { label: t('顶部菜单'), value: 'top' },
  { label: t('混合菜单'), value: 'mix' },
] as const;

const storageKey = 'nest-web:settings';

const prefersDark = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

export const defaultSettings = (): AppSettings => ({
  themeMode: prefersDark() ? 'dark' : 'light',
  primaryColor: defaultPrimaryColor,
  layout: 'mix',
  compact: false,
  contentWidth: 'Fluid',
  fixedHeader: false,
  fixSiderbar: true,
  splitMenus: false,
  siderMenuType: 'sub',
});

const isThemeMode = (value: unknown): value is ThemeMode =>
  value === 'light' || value === 'dark';
const isLayoutMode = (value: unknown): value is LayoutMode =>
  value === 'side' || value === 'top' || value === 'mix';
const isContentWidth = (value: unknown): value is ContentWidth =>
  value === 'Fluid' || value === 'Fixed';
const isSiderMenuType = (value: unknown): value is SiderMenuType =>
  value === 'sub' || value === 'group';
const isBoolean = (value: unknown): value is boolean =>
  typeof value === 'boolean';
const isColor = (value: unknown): value is string =>
  typeof value === 'string' && value !== '';

/**
 * Keeps the fields this app understands, one by one, and nothing else.
 *
 * `localStorage` is user-writable and survives deploys, so a value from an older version of
 * this app is expected — and so is a patch from ProLayout's settings drawer, which sends every
 * key it knows about (`navTheme`, `headerRender`, its own `menu`/`title` copies). An
 * unrecognized value falls back to `fallback` instead of taking the whole object down with it.
 *
 * Exported so that the drawer's updates pass through exactly the same gate as what is read
 * back out of storage: there is one definition of a valid `AppSettings`.
 */
export const sanitizeSettings = (
  value: unknown,
  fallback: AppSettings,
): AppSettings => {
  if (typeof value !== 'object' || value === null) return fallback;
  const stored = value as Record<string, unknown>;
  return {
    themeMode: isThemeMode(stored.themeMode)
      ? stored.themeMode
      : fallback.themeMode,
    primaryColor: isColor(stored.primaryColor)
      ? stored.primaryColor
      : fallback.primaryColor,
    layout: isLayoutMode(stored.layout) ? stored.layout : fallback.layout,
    compact: isBoolean(stored.compact) ? stored.compact : fallback.compact,
    contentWidth: isContentWidth(stored.contentWidth)
      ? stored.contentWidth
      : fallback.contentWidth,
    fixedHeader: isBoolean(stored.fixedHeader)
      ? stored.fixedHeader
      : fallback.fixedHeader,
    fixSiderbar: isBoolean(stored.fixSiderbar)
      ? stored.fixSiderbar
      : fallback.fixSiderbar,
    splitMenus: isBoolean(stored.splitMenus)
      ? stored.splitMenus
      : fallback.splitMenus,
    siderMenuType: isSiderMenuType(stored.siderMenuType)
      ? stored.siderMenuType
      : fallback.siderMenuType,
  };
};

const readSettings = (): AppSettings => {
  const fallback = defaultSettings();
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return fallback;
    const stored: unknown = JSON.parse(raw);
    const settings = sanitizeSettings(stored, fallback);
    if (typeof stored === 'object' && stored !== null) {
      const themeVersion = 'themeVersion' in stored ? stored.themeVersion : 0;
      const isLegacyNeutral =
        (themeVersion === 0 || themeVersion === 1) &&
        settings.primaryColor.toLowerCase() === '#222222';
      const isLegacyBlue =
        themeVersion === 0 && settings.primaryColor.toLowerCase() === '#1677ff';
      if (isLegacyNeutral || isLegacyBlue)
        return { ...settings, primaryColor: defaultPrimaryColor };
    }
    return settings;
  } catch {
    // A blocked or corrupted store must not stop the app from rendering.
    return fallback;
  }
};

type AppSettingsContextValue = {
  readonly settings: AppSettings;
  readonly update: (patch: Partial<AppSettings>) => void;
  readonly reset: () => void;
};

const AppSettingsContext = createContext<AppSettingsContextValue | null>(null);

export const AppSettingsProvider = ({ children }: PropsWithChildren) => {
  const [settings, setSettings] = useState<AppSettings>(readSettings);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({ ...settings, themeVersion: 2 }),
      );
    } catch {
      // Storage can be full or disabled; the setting still applies to this session.
    }
  }, [settings]);

  const update = useCallback((patch: Partial<AppSettings>) => {
    setSettings((current) => ({ ...current, ...patch }));
  }, []);

  const reset = useCallback(() => {
    setSettings(defaultSettings());
  }, []);

  const value = useMemo(
    () => ({ settings, update, reset }),
    [settings, update, reset],
  );

  return (
    <AppSettingsContext.Provider value={value}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = (): AppSettingsContextValue => {
  const value = useContext(AppSettingsContext);
  if (value === null) {
    throw new Error('useAppSettings 只能在 <AppSettingsProvider> 内使用');
  }
  return value;
};
